import { state } from "./interfaces/interfaces";
import type {
	IServiceMessage,
	ServiceStates,
	ServiceWorkers,
} from "./services/serviceManager/interfaces";
import { serviceManager } from "./services/workers";
import type CacheUtils from "./utilities/cache";
import type { CacheTables } from "./utilities/cache";

export const createWorkerWebsocket = (
	setter: any,
	name: ServiceWorkers,
): [ServiceWorkers, (e: MessageEvent<IServiceMessage>) => void] => [
	name,
	(e: MessageEvent<IServiceMessage>) => {
		// check if this is service message
		if (e.data && e.data.serviceName) {
			const { serviceData, serviceName } = e.data; // e.data is the message from the web worker
			const { command, payload } = serviceData; // command, payload

			if (serviceName === name) {
				// if the message is for this service

				if (command === "SET_SERVICE_STATE") {
					setServiceState(payload, name);
					return;
				}

				if (command === "SET_RESULT") {
					setter[payload.target][payload.property] = JSON.parse(payload.data);
					return;
				}
			}
		}
	},
];

export const createLoggerWorker = (
	name: ServiceWorkers,
): [ServiceWorkers, (e: MessageEvent<IServiceMessage>) => void] => [
	name,
	(e: MessageEvent<IServiceMessage>) => {
		// check if this is service message
		if (e.data && e.data.serviceName) {
			const { serviceData, serviceName, promiseId } = e.data; // e.data is the message from the web worker
			const { command, payload } = serviceData; // command, payload

			if (serviceName === name) {
				// if the message is for this service
				// create a message to send to the worker
				const message: IServiceMessage = {
					serviceData: {
						command,
						payload,
						origin: "MAIN_THREAD",
					},
					serviceName,
					promiseId: promiseId,
				};

				if (command === "SET_SERVICE_STATE") {
					setServiceState(payload, name);
					return;
				}

				// MAIN THREAD -> WORKER (request)
				serviceManager.sendMessageToWorker(message);
				return;
			}
		}
	},
];

export const createWorkerBookingsSync = (
	setter: any,
	name: ServiceWorkers,
): [ServiceWorkers, (e: MessageEvent<IServiceMessage>) => void] => [
	name,
	async (e: MessageEvent<IServiceMessage>) => {
		// check if this is service message
		if (e.data && e.data.serviceName) {
			const { serviceData, serviceName, promiseId } = e.data; // e.data is the message from the web worker
			const { command, payload, origin, error } = serviceData; // command, payload, origin, error

			if (serviceName === name) {
				// if the message is for this service
				// create a message to send to the worker

				if (promiseId && origin === "WORKER_THREAD") {
					// if this is a response
					const { resolve, reject } = serviceManager.getPromise(promiseId);
					if (error) {
						reject(error);
					} else {
						resolve(payload);
					}
					serviceManager.deletePromise(promiseId);
					return;
				}

				if (command === "BOOKINGS_IN_PROGRESS") {
					setter[name] = payload;
					return;
				}

				if (command === "UPDATE_VERSIONS") {
					setter[name] = payload;
					return;
				}

				if (command === "GET_BOOKING") {
					serviceManager.sendMessageToWorker({
						serviceName: name,
						serviceData: {
							origin: "MAIN_THREAD",
							command: "GET_BOOKING",
							payload,
						},
					});
				}

				if (command === "SET_SERVICE_STATE") {
					setServiceState(payload, name);
					return;
				}
			}
		}
	},
];

export const createWorkerCache = (
	setter: CacheUtils,
	name: ServiceWorkers,
): [ServiceWorkers, (e: MessageEvent<IServiceMessage>) => void] => [
	name,
	(e: MessageEvent<IServiceMessage>) => {
		// check if this is service message
		if (e.data && e.data.serviceName) {
			const { serviceData, serviceName } = e.data; // e.data is the message from the web worker
			const { command, payload, origin } = serviceData; // command, payload, origin

			if (serviceName === name) {
				// if the message is for this service
				// check if this is a response from the worker
				if (origin !== "WORKER_THREAD") return;

				if (command === "SET_CACHE_DATA") {
					for (const key in payload) {
						if (Object.hasOwn(payload, key)) {
							const element = payload[key];
							setter.setCache(key as CacheTables, element);
						}
					}
					return;
				}
				if (command === "REFRESHING_CACHE") {
					state.CacheLoading = true;
				}
				if (command === "REFRESHING_COMPLETE") {
					state.CacheLoading = false;
				}

				if (command === "SET_SERVICE_STATE") {
					setServiceState(payload, name);
					return;
				}
			} else {
				// if the message is not for this service, it is a request from the worker to another service
				// create a message to send to the worker
				const { promiseId } = e.data;
				const message: IServiceMessage = {
					serviceData: {
						command,
						payload,
						origin: "MAIN_THREAD",
					},
					serviceName,
					promiseId: promiseId,
				};

				// MAIN THREAD -> WORKER (request)
				serviceManager.sendMessageToWorker(message);
			}
		}
	},
];

export const setServiceState = (state: ServiceStates, name: ServiceWorkers) => {
	if (state !== "LAST_RUN") serviceManager.setServiceState(name, state); // set the service state in the service manager
	if (state === "RUNNING") {
		// if the state is running
		serviceManager.setStartedAt(name);
	}

	if (state === "STOPPED") {
		// if the state is stopped
		serviceManager.setStoppedAt(name);
	}

	if (state === "LAST_RUN") {
		// if the state is last run
		serviceManager.setLastRun(name);
	}
};
